import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {Card} from '@jobseeker/lotus/components';
import {Facebook, LinkedIn, Twitter} from '@jobseeker/lotus/assets/icons';
import {Link} from '@mui/material';
import {useSelector, shallowEqual} from 'react-redux';
import {useInterceptor} from '@jobseeker/common-core';
import {Image} from '@jobseeker/lotus/components/Image';
import {InterceptorType} from './types';
import {Paragraph} from '@jobseeker/lotus/elements';
import InjectSearchOverlay from '@components/SearchInjection';

import type {AppState} from '@jobseeker/common-core/types';
import {useWrappedTheme} from '@jobseeker/lotus/hooks/common';

const AboutUs = () => {
  const interceptor: InterceptorType = useInterceptor() as InterceptorType;
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const {
    footer: {socialLinks},
  } = interceptor;

  const SOCIAL_MEDIA_SRC: {[key: string]: string} = {
    linkedin: isAppIimjobs
      ? LinkedIn
      : 'https://job-static.hirist.com/V8/static/media/linkedin-icon.83a9eb42.svg',
    facebook: isAppIimjobs
      ? Facebook
      : 'https://job-static.hirist.com/V8/static/media/fb-icon.9a471ab6.svg',
    twitter: isAppIimjobs
      ? Twitter
      : 'https://job-static.hirist.com/V8/static/media/twitter-icon.b08b5a4c.svg',
  };

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  const theme = useWrappedTheme();

  const currentYear = new Date().getFullYear();
  const PORTAL = `${process.env.NEXT_PUBLIC_APP_NAME}`;
  const DOMAIN = `${process.env.NEXT_PUBLIC_APP_DOMAIN}`;
  return isAppIimjobs ? (
    <Box
      sx={{
        maxWidth: '1050px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
      <Card variant="" heading="">
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: theme.typography.pxToRem(24),
            fontWeight: 500,
            lineHeight: '30px',
            textAlign: 'Center',
            color: 'secondary.dark',
            marginBottom: 5,
          }}
        >
          ABOUT US
        </Typography>
        <Paragraph
          title=""
          body={
            <>
              <i>
                <b>
                  &quot;So many of our dreams at first seem impossible, then
                  they seem improbable, and then, when we summon the will, they
                  soon become inevitable.&quot;
                </b>
              </i>
            </>
          }
        />
        <Paragraph
          title=""
          body={
            <>
              It starts with a seed of an idea. Then that seed germinates into a
              thought. And finally, that thought begins to grow in your mind
              like a tree seeking sunshine!
            </>
          }
        />
        <Paragraph
          title=""
          body={
            <>
              It was with a similar idea that {PORTAL} was born and now that
              idea has grown into a platform that helps jobseekers connect with
              their dream job. We&apos;ve connected with over 25,00,000
              jobseekers already and this is just the beginning...
            </>
          }
        />
        <Paragraph
          title=""
          body={
            <>
              We feature{' '}
              <Typography
                component={'span'}
                sx={{
                  textDecoration: 'line-through',
                }}
              >
                some of
              </Typography>{' '}
              the best jobs in{' '}
              <b>
                Banking & Finance, Consulting, Research & Analytics, Sales &
                Marketing, HR, IT and Operations.
              </b>
            </>
          }
        />
        <Paragraph
          title=""
          body={
            <>
              We&apos;re a team of{' '}
              <b> thinkers, creators, technologists and mavericks </b> but most
              importantly, we&apos;re a team of <b>doers!</b>
              <br />
              We are young, we&apos;re passionate and we&apos;re shaking up the
              recruitment space in India.
            </>
          }
        />
        <Paragraph title="" body="" />
        <Paragraph title="" body="You should connect with us. Seriously!" />
        <Box>
          {socialLinks.map((socialMediaInfo, index) => (
            <Link
              key={index}
              href={socialMediaInfo.url}
              target="_blank"
              sx={{marginRight: 1, display: 'inline-block'}}
            >
              <Image
                src={SOCIAL_MEDIA_SRC[socialMediaInfo.title]}
                alt={socialMediaInfo.title}
                width={30}
                height={30}
              />
            </Link>
          ))}
        </Box>
        <Paragraph
          title=""
          body={
            <>
              {DOMAIN} &#169; {currentYear}. All rights reserved.
            </>
          }
        />
      </Card>
    </Box>
  ) : (
    <Box
      sx={{
        mt: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 400,
        py: {
          xxs: 1.5,
          md: 5,
          lg: 5,
        },
        px: {
          xxs: 1.25,
          md: 3.75,
          lg: 6.25,
        },

        marginTop: 0,
      }}
    >
      <Card
        variant=""
        heading=""
        sx={{
          pt: {
            md: 2.5,
            xs: 2.25,
          },
          pb: {
            md: 6.625,
            xs: 2.25,
          },
          px: {
            md: 3.125,
            xs: 1.5,
          },
          borderRadius: !isAppIimjobs ? '8px' : '15px',
          width: !isAppIimjobs ? '100%' : 'inherit',

          padding: 2.5,
          minHeight: '70vh',
        }}
      >
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
            }
          }
          title=""
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
            }
          }
          body={
            <>
              &quot;
              <i>
                So many of our dreams at first seem impossible, then they seem
                improbable—and then, when we summon the will, they become
                inevitable.
              </i>
              &quot;
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),

              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
            }
          }
          title=""
          body={
            <>
              Every meaningful career journey begins with a single idea—a spark
              that grows into ambition, direction, and purpose.
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),

              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              <b>{process.env.NEXT_PUBLIC_APP_DOMAIN}</b> was built on that very
              belief.
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              <p>
                What started as a simple idea has evolved into a focused
                technology hiring platform designed to help high-quality tech
                professionals discover roles that truly match their skills,
                aspirations, and potential.
              </p>
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),

              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              <b>Today, hirist.tech connects more than 35 lacs of jobseekers</b>{' '}
              with curated opportunities at some of the most exciting
              technology-driven companies—and we are only getting started.
            </>
          }
        />
        <>
          <Paragraph
            style={
              !isAppIimjobs && {
                fontSize: theme.typography.pxToRem(16),
                lineHeight: '24px',
              }
            }
            titleStyle={
              !isAppIimjobs && {
                fontSize: theme.typography.pxToRem(15),
                fontWeight: 'bold',
              }
            }
            title=""
            body={
              <>
                We specialize exclusively in technology roles across high-demand
                domains, including:
              </>
            }
          />
          <ul
            style={{
              marginTop: 1,
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
              fontWeight: 'bold',
            }}
          >
            <li>Artificial Intelligence &amp; Machine Learning</li>
            <li>Data Analytics &amp; Data Engineering</li>
            <li>Backend, Frontend &amp; Full-Stack Development</li>
            <li>Mobile Application Development</li>
            <li>DevOps &amp; Cybersecurity</li>
            <li>Emerging Technologies (Blockchain, AR/VR, IoT)</li>
            <li>Product Design (UI/UX)</li>
            <li>Quality Assurance</li>
          </ul>
        </>

        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              Our mission is clear:{' '}
              <b>
                to reduce noise in tech hiring and create meaningful,
                high-signal connections between premium talent and
                career-defining opportunities.
              </b>
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              Behind hirist.tech is a team of thinkers, creators, technologists,
              and problem-solvers who believe execution matters as much as
              ideas. We build with intent, move with speed, and remain deeply
              focused on delivering outcomes—for jobseekers and recruiters
              alike.
            </>
          }
        />
        <Paragraph
          style={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(16),
              lineHeight: '24px',
            }
          }
          titleStyle={
            !isAppIimjobs && {
              fontSize: theme.typography.pxToRem(15),
              fontWeight: 'bold',
            }
          }
          title=""
          body={
            <>
              Because great careers are not found by chance—they are built with
              purpose.
            </>
          }
        />
        <Box>
          {socialLinks.map((socialMediaInfo, index) => (
            <Link
              key={index}
              href={socialMediaInfo.url}
              target="_blank"
              sx={{
                marginRight: isAppIimjobs ? 1 : 2.5,
                display: 'inline-block',
              }}
            >
              <Image
                src={SOCIAL_MEDIA_SRC[socialMediaInfo.title]}
                alt={socialMediaInfo.title}
                width={isAppIimjobs ? 30 : 42}
                height={isAppIimjobs ? 30 : 42}
              />
            </Link>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default AboutUs;
